import { Plus } from "lucide-react";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { ItemList } from "@/shared/components/ui/ItemList";
import { formatBusinessTime } from "@/shared/utils/formatters";
import type { Appointment } from "../types/appointmentTypes";
import { AppointmentListCard } from "./AppointmentListCard";

interface AppointmentsListProps {
  appointments: Appointment[];
  onAppointmentClick: (appointment: Appointment) => void;
  onGapAdd?: (startAt: Date) => void;
  showGaps?: boolean;
  isLoading?: boolean;
  error?: Error | null;
}

export function AppointmentsList({ appointments, onAppointmentClick, onGapAdd, showGaps = true, isLoading, error }: AppointmentsListProps) {
  const { business } = useCurrentBusiness();
  const sorted = [...appointments].sort((a, b) => (a.startAt?.getTime() ?? Infinity) - (b.startAt?.getTime() ?? Infinity));
  return (
    <ItemList
      items={sorted}
      isLoading={isLoading}
      error={error}
      emptyState={{ message: "No appointments for this day." }}
      renderItem={(appointment) => {
        const index = sorted.indexOf(appointment);
        const next = sorted[index + 1];
        const active = ["pending", "confirmed"].includes(appointment.status) && next && ["pending", "confirmed"].includes(next.status);
        const occupiedUntil = Math.max(...sorted.slice(0, index + 1).filter((item) => ["pending", "confirmed"].includes(item.status)).map((item) => item.endAt?.getTime() ?? 0));
        const gap = active && next.startAt ? Math.floor((next.startAt.getTime() - occupiedUntil) / 60_000) : 0;
        return (
          <div className="space-y-2">
            <AppointmentListCard appointment={appointment} onClick={onAppointmentClick} />
            {showGaps && gap > 0 && Number.isFinite(occupiedUntil) && (
              <div className="flex min-h-12 items-center gap-2 rounded-xl px-3 text-xs" style={{ backgroundImage: "repeating-linear-gradient(45deg,transparent,transparent 4px,var(--muted) 4px,var(--muted) 8px)" }}>
                <span className="rounded-lg bg-card p-2 font-semibold">{formatBusinessTime(new Date(occupiedUntil), business?.timezone ?? "UTC")}</span>
                <span className="rounded bg-card px-1.5 py-1 text-primary">{gap < 60 ? `${gap} min` : `${Math.floor(gap / 60)}h${gap % 60 ? ` ${gap % 60}m` : ""}`}</span>
                {onGapAdd && <Button variant="quiet" className="ml-auto" aria-label="Add appointment in this gap" onClick={() => onGapAdd(new Date(occupiedUntil))}><Plus aria-hidden="true" />Add</Button>}
              </div>
            )}
          </div>
        );
      }}
    />
  );
}
